import React, { useEffect, useState } from "react";

import { Dialog, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import { useUpdateCareAppointmentNoteMutation } from "@api/graphql/types-and-hooks";

interface EditAppointmentNotesDialogProps {
  open: boolean;
  onClose: () => void;
  appointmentId: string;
  currentNotes: string;
  onSuccess?: () => void;
}

export const EditAppointmentNotesDialog: React.FC<EditAppointmentNotesDialogProps> = ({
  open,
  onClose,
  appointmentId,
  currentNotes,
  onSuccess,
}) => {
  const [notes, setNotes] = useState(currentNotes || "");
  const [error, setError] = useState<string | null>(null);

  const { mutate: updateNotes, isPending: isLoading } = useUpdateCareAppointmentNoteMutation();

  // Reset notes when dialog opens with new currentNotes
  useEffect(() => {
    if (open) {
      setNotes(currentNotes || "");
      setError(null);
    }
  }, [open, currentNotes]);

  const handleSave = async () => {
    if (notes.length > 500) {
      setError("Notes must be 500 characters or less");
      return;
    }

    updateNotes(
      {
        appointmentId,
        notes,
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
        onError: (err: unknown) => {
          const message = err instanceof Error ? err.message : "Failed to update notes";
          setError(message);
        },
      },
    );
  };

  const handleCancel = () => {
    setNotes(currentNotes || "");
    setError(null);
    onClose();
  };

  const remainingChars = 500 - notes.length;
  const isOverLimit = remainingChars < 0;

  return (
    <Dialog
      size="lg"
      open={open}
      setOpen={onClose}
      title="Edit Appointment Notes"
      primaryButton={{
        text: isLoading ? "Saving..." : "Save",
        onClick: handleSave,
        disabled: isLoading || isOverLimit,
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: handleCancel,
        disabled: isLoading,
      }}
    >
      <div className="space-y-4">
        <div>
          <Label className="block text-sm font-medium text-gray-700 mb-2">Notes</Label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full min-h-[200px] p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Enter appointment notes..."
            disabled={isLoading}
          />
          <div className="mt-2 flex justify-between items-center">
            <Paragraph size="small" colorType="secondary" className={isOverLimit ? "text-red-600" : "text-gray-500"}>
              {remainingChars} characters remaining
            </Paragraph>
          </div>
        </div>

        {error && (
          <Paragraph colorType="secondary" className="text-red-600 text-sm">
            Error: {error}
          </Paragraph>
        )}
      </div>
    </Dialog>
  );
};
